
import moment from 'moment-timezone';
import { useQuery } from 'react-query';
import { useConfig } from '../components/ConfigContext';
import { useState } from 'react';
import { isNumber } from 'lodash';

import { getQueryStatus, load } from '../lib';

const getQuery = (filters) => {
  const { teamId, groupId, officeId, startDate, ...restFilters } = filters;
  const gameFilters = Object.keys(restFilters)
    .reduce((filters, key) => {
      const value = restFilters[key];
      if (value != null) filters.push({ [key]: value });
      return filters;
    }, [])

  if (teamId) {
    gameFilters.push({ or: [
      { homeTeamId: teamId },
      { awayTeamId: teamId },
    ] })
  }

  if (groupId) {
    gameFilters.push({ or: [
      { groupId },
      { groupId: null }
    ] })
  }

  if (startDate) {
    if (startDate.gte) {
      gameFilters.push({
        date: { gte: moment.utc(startDate.gte, 'YYYY-MM-DD').toISOString() }
      })
    } else {
      gameFilters.push({
        date: moment.utc(startDate, 'YYYY-MM-DD').toISOString()
      })
    }
  }

  if (!gameFilters?.length) return { officeId }

  return {
    officeId,
    and: gameFilters,
  }
}

const getInitialPage = (pastCount, totalCount, perPage) => {
  if (pastCount == null || totalCount == null) return;

  const initialPage = pastCount ? Math.ceil(pastCount / perPage) : 1
  const lastPage = Math.ceil(totalCount / perPage) || 1;
  return initialPage <= lastPage ? initialPage : lastPage;
}

const loadTotalCount = (filters, apiUrl) => {
  const request = load(`${apiUrl}/games/count`, {
    where: getQuery(filters)
  })
  const result = request.then(result => result.count)

  result.cancel = request.cancel;
  return result;
}

const loadPastCount = (filters, apiUrl) => {
  if (filters && filters.startDate && !filters.startDate.gte) return Promise.resolve(1);

  const { officeId, ...gameFilter } = getQuery(filters)
  const request = load(`${apiUrl}/games/count`, {
    where: {
      officeId,
      ...gameFilter,
      startTime: { lte: new Date() },
    }
  })
  const result = request.then(result => result.count + 1)

  result.cancel = request.cancel;
  return result;
}

const loadUpcomingCount = (filters, apiUrl) => {
  const { officeId, ...gameFilter } = getQuery(filters)
  const request = load(`${apiUrl}/games/count`, {
    where: {
      officeId,
      ...gameFilter,
      startTime: { gte: new Date() },
    }
  })
  const result = request.then(result => result.count + 1)

  result.cancel = request.cancel;
  return result;
}

const loadGames = (filters, currentPage, perPage, queryLimit, apiUrl) => {
  let limit = queryLimit || perPage;
  let skip = (currentPage - 1) * perPage;
  if (limit < 0) {
    limit = 0;
  }
  if (skip < 0) {
    skip = 0;
  }

  const request = load(`${apiUrl}/games`, {
    filter: {
      where: getQuery(filters),
      include: [
        'surface',
        'schedule',
        'group',
        'teamStats',
      ],
      order: ['startTime ASC', 'id DESC'],
      limit,
      skip
    }
  })

  const result = request
    .then(games => {
      const teamIds = games.flatMap(game => [game.homeTeamId, game.awayTeamId])
      return Promise.all([
        games,
        // TODO: split into multiple queries
        load(`${apiUrl}/teams`, {
          filter: {
            where: { id: { inq: teamIds } },
            scope: 'Tenant',
          }
        })
      ])
    })
    .then(([ games, teams ]) => {
      return games.map(game => {
        game.homeTeam = teams.find(team => team.id === game.homeTeamId);
        game.awayTeam = teams.find(team => team.id === game.awayTeamId);
        return game;
      })
    })

  result.cancel = request.cancel;
  return result;
}

export const useGames = (filters, { currentPage, perPage, queryLimit, totalOptions, initialOptions, ...options }) => {
  const { apiUrl } = useConfig();

  const { data: total, status: totalStatus } = useQuery(
    ['games-total', filters],
    () => loadTotalCount(filters, apiUrl),
    totalOptions
  )

  const { data: pastCount, status: initialStatus } = useQuery(
    ['games-initial', filters],
    () => loadPastCount(filters, apiUrl),
    initialOptions
  )

  const { status: dataStatus, ...data } = useQuery(
    ['games-data', filters, currentPage, perPage],
    () => loadGames(filters, currentPage, perPage, queryLimit, apiUrl),
    { enabled: currentPage != null, ...options }
  )

  const initialPage = getInitialPage(pastCount, total, perPage);
  const status = getQueryStatus(totalStatus, initialStatus, dataStatus);
  return { initialPage, total, status, ...data };
}

export const useTeamGamesView = (filters, limit = 5) => {
  const { apiUrl } = useConfig();
  const [currentState, setCurrentState] = useState('initial')
  const [startDate, setStartDate] = useState({ gte: moment.utc(new Date(), 'YYYY-MM-DD').toISOString() });
  const [queryLimit, setQueryLimit] = useState(limit);
  const [buttonText, setButtonText] = useState('common.lang.show.all.upcoming.games');

  // Set filters startDate to default view startDate
  filters.startDate = startDate;

  const nextState = () => {
    switch (currentState) {
      case 'initial':
        changeState('upcoming');
        break;
      case 'upcoming':
        changeState('all');
        break;
    }
  }

  const changeState = (newState) => {
    setCurrentState(newState);

    switch (newState) {
      case 'all':
        setStartDate(null);
        setQueryLimit(null);
        setButtonText('common.lang.show.all.upcoming.games');
        break;
      case 'initial':
        setStartDate({ gte: moment.utc(new Date(), 'YYYY-MM-DD').toISOString() });
        setQueryLimit(limit);
        setButtonText('common.lang.show.all.upcoming.games')
        break;
      case 'upcoming':
        setStartDate({ gte: moment.utc(new Date(), 'YYYY-MM-DD').toISOString() })
        setQueryLimit(null);
        setButtonText('common.lang.show.all.games');
        break;
    }
  }

  const { data: total } = useQuery(
    ['games-total', filters],
    () => loadTotalCount(filters, apiUrl)
  )

  if (isNumber(total) && total <= queryLimit && currentState === 'initial') {
    changeState('upcoming');
  }

  return { currentState, startDate, queryLimit, buttonText, total, nextState, changeState }
}
